"use client"

import * as React from "react"
import { Plus, Search, FileText, Handshake, Users, FolderOpen, Loader, LayoutDashboard } from "lucide-react"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import { Button } from "./Button"
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxTrigger,
  ComboboxStatus,
} from "./combobox"
import { cn } from "@/lib/utils"

// Context item types
export type ContextItemType = "contract" | "document" | "person" | "folder" | "page"

export interface ContextItem {
  id: string
  type: ContextItemType
  label: string
  value: string
  description?: string
}

// Pages available as context items
export const PAGE_CONTEXT_ITEMS: ContextItem[] = [
  { id: "page-dashboard",        type: "page", label: "Dashboard",        value: "/dashboard",        description: "Overview" },
  { id: "page-vault",            type: "page", label: "Vault",            value: "/vault",            description: "Contracts & documents" },
  { id: "page-deal-pipeline",    type: "page", label: "Deal Pipeline",    value: "/deal-pipeline",    description: "Active deals" },
  { id: "page-people",           type: "page", label: "People",           value: "/people",           description: "Contacts" },
  { id: "page-inbox",            type: "page", label: "Inbox",            value: "/inbox",            description: "Messages" },
  { id: "page-calendar",         type: "page", label: "Calendar",         value: "/calendar",         description: "Schedule" },
  { id: "page-roster",           type: "page", label: "Roster",           value: "/roster",           description: "Client roster" },
  { id: "page-delivery-manager", type: "page", label: "Delivery Manager", value: "/delivery-manager", description: "Deliveries" },
  { id: "page-integrations",     type: "page", label: "Integrations",     value: "/integrations",     description: "Connected apps" },
  { id: "page-ask-ari",          type: "page", label: "Ask Ari",          value: "/chat",             description: "Chat and create" },
]

/** Returns the page context item matching the current pathname, stripping /demo prefix. */
export function getPageContextItem(pathname: string): ContextItem | null {
  const normalised = pathname.replace(/^\/demo/, "") || "/"
  return PAGE_CONTEXT_ITEMS.find(
    p => normalised === p.value || normalised.startsWith(p.value + "/")
  ) ?? null
}

// Demo-only fixture items (deals, documents, people, folders). These are
// SHOWCASE data for /demo/* — they must NEVER render on a real route, where the
// real deals arrive via `extraItems` and the only static items are the genuine
// nav links in PAGE_CONTEXT_ITEMS. Gated behind route-authoritative useIsDemo().
const DEMO_FIXTURE_ITEMS: ContextItem[] = [
  // Contracts
  { id: "contract-1", type: "contract", label: "Artist Recording Deal", value: "artist-recording-deal", description: "Mayowa × Def Jam" },
  { id: "contract-2", type: "contract", label: "Producer Agreement", value: "producer-agreement", description: "Beat licensing terms" },
  { id: "contract-3", type: "contract", label: "Brand Partnership (NIL)", value: "brand-partnership-nil", description: "Nike endorsement" },
  { id: "contract-4", type: "contract", label: "SAFE Agreement", value: "safe-agreement", description: "Investor terms" },
  { id: "contract-5", type: "contract", label: "Publishing Deal", value: "publishing-deal", description: "Sony/ATV" },
  { id: "contract-6", type: "contract", label: "Management Agreement", value: "management-agreement", description: "Roc Nation" },

  // Documents
  { id: "doc-1", type: "document", label: "Royalty Statement Q4", value: "royalty-statement-q4", description: "Oct - Dec 2024" },
  { id: "doc-2", type: "document", label: "Tour Budget 2025", value: "tour-budget-2025", description: "North America" },
  { id: "doc-3", type: "document", label: "Merchandise Agreement", value: "merch-agreement", description: "Live Nation" },
  { id: "doc-4", type: "document", label: "Sync License - Apple", value: "sync-license-apple", description: "iPhone campaign" },
  { id: "doc-5", type: "document", label: "Distribution Agreement", value: "distribution-agreement", description: "DistroKid" },

  // People
  { id: "person-1", type: "person", label: "Adam Silver", value: "adam-silver", description: "Manager" },
  { id: "person-2", type: "person", label: "Roger Goodell", value: "roger-goodell", description: "Legal counsel" },
  { id: "person-3", type: "person", label: "Sarah Chen", value: "sarah-chen", description: "A&R Rep" },
  { id: "person-4", type: "person", label: "Marcus Williams", value: "marcus-williams", description: "Producer" },
  { id: "person-5", type: "person", label: "Jennifer Lee", value: "jennifer-lee", description: "Booking agent" },

  // Folders
  { id: "folder-1", type: "folder", label: "Recording Contracts", value: "recording-contracts", description: "12 documents" },
  { id: "folder-2", type: "folder", label: "Publishing Deals", value: "publishing-deals", description: "8 documents" },
  { id: "folder-3", type: "folder", label: "Brand Partnerships", value: "brand-partnerships", description: "5 documents" },
  { id: "folder-4", type: "folder", label: "Legal Documents", value: "legal-documents", description: "23 documents" },
  { id: "folder-5", type: "folder", label: "Financial Reports", value: "financial-reports", description: "15 documents" },
]

// Full demo set: rich fixtures + the genuine nav links. Used ONLY on /demo/*.
const DEMO_CONTEXT_ITEMS: ContextItem[] = [
  ...DEMO_FIXTURE_ITEMS,
  ...PAGE_CONTEXT_ITEMS,
]

export const TYPE_ICONS: Record<ContextItemType, typeof FileText> = {
  contract: Handshake,
  document: FileText,
  person: Users,
  folder: FolderOpen,
  page: LayoutDashboard,
}

const TYPE_LABELS: Record<ContextItemType, string> = {
  contract: "Contract",
  document: "Document",
  person: "Person",
  folder: "Folder",
  page: "Page",
}

// Async search over a supplied source list (simulated delay preserved so the
// loading affordance behaves identically in both modes). The SOURCE is what
// differs by route: demo uses the rich fixtures, the real route uses only the
// genuine nav links (real deals arrive separately via extraItems).
async function searchContextItems(query: string, source: ContextItem[]): Promise<ContextItem[]> {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 300))

  if (!query.trim()) {
    return source
  }

  const lowerQuery = query.toLowerCase()
  return source.filter(item =>
    item.label.toLowerCase().includes(lowerQuery) ||
    item.description?.toLowerCase().includes(lowerQuery) ||
    item.type.toLowerCase().includes(lowerQuery)
  )
}

interface ChatContextSelectorProps {
  value: ContextItem[]
  onChange: (items: ContextItem[]) => void
  /** Dynamic items (e.g. deal/analysis pages) injected from the parent */
  extraItems?: ContextItem[]
}

export function ChatContextSelector({ value, onChange, extraItems = [] }: ChatContextSelectorProps) {
  const isDemo = useIsDemo()
  // Route-authoritative source list. On /demo/* the rich fixtures showcase the
  // product; on a real route the ONLY static items are the genuine nav links —
  // the real deals/documents arrive via `extraItems` (dynamicPageItems). No demo
  // deal/people/category fixtures ever leak onto a real route.
  const sourceItems = isDemo ? DEMO_CONTEXT_ITEMS : PAGE_CONTEXT_ITEMS
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [items, setItems] = React.useState<ContextItem[]>(sourceItems)
  const [loading, setLoading] = React.useState(false)

  // Debounced search — merges extraItems (dynamic pages) into results
  React.useEffect(() => {
    const controller = new AbortController()
    setLoading(true)

    const timeoutId = setTimeout(async () => {
      try {
        const results = await searchContextItems(inputValue, sourceItems)
        if (!controller.signal.aborted) {
          // Merge extraItems: prepend those not already in static results
          const staticIds = new Set(results.map(r => r.id))
          const filtered = extraItems.filter(e => {
            if (staticIds.has(e.id)) return false
            if (!inputValue.trim()) return true
            const q = inputValue.toLowerCase()
            return e.label.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q)
          })
          setItems([...filtered, ...results])
          setLoading(false)
        }
      } catch {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }, 150)

    return () => {
      controller.abort()
      clearTimeout(timeoutId)
    }
  }, [inputValue, extraItems, sourceItems])

  const handleSelect = (item: ContextItem | ContextItem[] | null) => {
    if (!item) return
    
    // Handle array (multiple selection mode returns array)
    const selectedItems = Array.isArray(item) ? item : [item]
    onChange(selectedItems)
  }

  return (
    <Combobox
      open={open}
      onOpenChange={setOpen}
      value={value}
      onValueChange={handleSelect}
      multiple
      items={items}
    >
      <ComboboxTrigger
        render={
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 text-sm font-normal text-muted-foreground hover:text-foreground"
          />
        }
      >
        <Plus className="size-3.5" />
        Add context
      </ComboboxTrigger>
      
      <ComboboxPopup className="w-72" sideOffset={8}>
        <div className="border-b p-2">
          <div className="relative">
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
              {loading ? (
                <Loader className="size-4 animate-spin text-muted-foreground" />
              ) : (
                <Search className="size-4 text-muted-foreground" />
              )}
            </div>
            <ComboboxInput
              className="w-full rounded-md before:rounded-[calc(var(--radius-md)-1px)] pl-8"
              placeholder="Search contracts, documents…"
              showTrigger={true}
              size="sm"
            />
          </div>
        </div>
        
        <ComboboxEmpty>No items found.</ComboboxEmpty>
        
        <ComboboxList>
          {(item: ContextItem) => {
            const Icon = TYPE_ICONS[item.type]
            const isSelected = value.some(v => v.id === item.id)
            
            return (
              <ComboboxItem
                key={item.id}
                value={item}
                className={cn(isSelected && "bg-accent/80")}
              >
                <div className="flex items-center gap-2 w-full">
                  {/* <Icon className="size-4 shrink-0 text-muted-foreground" /> */}
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{item.label}</div>
                    {item.description && (
                      <div className="text-xs text-muted-foreground truncate">
                        {item.description}
                      </div>
                    )}
                  </div>
                  
                </div>
              </ComboboxItem>
            )
          }}
        </ComboboxList>
        
        {loading && (
          <ComboboxStatus>
            Searching…
          </ComboboxStatus>
        )}
      </ComboboxPopup>
    </Combobox>
  )
}

// Export types and demo data for external use
export { DEMO_CONTEXT_ITEMS }

